import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';

import './PeriodReportPDF.scss';
import InnerMctMonitoring from './components/InnerMctMonitoring';
import InnerSbsMonitoring from './components/InnerSbsMonitoring';
import InnerSTMonitoring from './components/InnerSTMonitoring';
import TrapConnectivityGaps from './components/TrapConnectivityGaps';

import useGetUser from 'hooks/VisitReport/useGetUser';
import { getPeriodReports } from 'api/periodReports/getPeriodReports';
import { MLButton } from 'components/Button/Button';
import { TrapTimeLineChart } from 'pages/VisitReports/Charts/TrapsTimeLineChart';
import { SBSChart } from 'pages/VisitReports/Charts/SBSChart';
import trojanLogo from 'assets/images/trojan_logo.svg';
import SignatureModal from 'components/SignatureModal/SignatureModal';
import { useManageSignatureModalStore } from 'components/SignatureModal/ManageSignatureModalStore';
import useSignPeriodReportByEmployee from 'hooks/PeriodReports/useSignPeriodReportByEmployee';
import { TrapTypeMonitoring } from 'api/reports/types';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import HeatMaps from 'pages/VisitReports/PDFComponents/HeatMaps';

const PeriodDocsPDF = () => {
  const { onManageSignatureModalOpen } = useManageSignatureModalStore();
  let { slug, id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useQuery('periodDocs', () => getPeriodReports({ slug: slug || '', id: id || '' }), {});
  const { data: userData, refetch } = useGetUser();
  const { signPeriodReportByEmployee } = useSignPeriodReportByEmployee();
  const printPDF = () => {
    window.print();
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page { margin: 0; size: portrait; }
        
        body * {
          visibility: hidden;
        }
        
        #reportPrintPDF, #reportPrintPDF * {
          visibility: visible;
        }
        
        #reportPrintPDF {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          margin: 0;
        }
        
        .row-print {
          display: flex;
          justify-content: center;
          margin: 0;
        }
        
        .col-print {
          flex: 0 0 66.666667%;
          max-width: 66.666667%;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="page-content bg-light">
      <Container fluid>
        <MLButton
          innerText="Print PDF"
          margin={1}
          color="success"
          fontSize={18}
          isGhost
          icon="ri-file-pdf-fill fs-16"
          onClick={printPDF}
        />
        <MLButton
          innerText="Back to Docs"
          margin={1}
          color="info"
          fontSize={18}
          isGhost
          icon="ri-file-pdf-fill fs-16"
          onClick={() => {
            navigate(`/period-report/${slug}/${id}/`);
          }}
        />
        <div className="card-body-ml" id="reportPrintPDF">
          <div className="row">
            <div className="col-lg-9">
              <div className="d-flex justify-content-between align-items-center">
                <img src={trojanLogo} alt="" height="32" />
              </div>
            </div>
            <div className="col-lg-3" style={{ borderBottom: '1px solid black', margin: '10px 0' }} />
          </div>
          <div className="row mt-5">
            <div className="header-container">
              <h3>
                <strong>
                  {t('PeriodReports.page-print-desc1')}
                  {id}
                </strong>
              </h3>
              <h5 className="white-space: nowrap; margin-left: 10px;">
                &nbsp; {t('PeriodReports.page-print-desc2')} {data?.start_date} - {data?.end_date}
              </h5>
            </div>
          </div>
          <div className="row mt-3">
            <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
              <tbody className="list form-check-all">
                <tr>
                  <th scope="row">{t('PeriodReportPDF.header.report-date')}</th>
                  <td className="text-start p-1 m-0">
                    <strong>{dayjs(data?.call_date).format('YYYY-MM-DD')}</strong>
                  </td>
                </tr>
                <tr>
                  <th scope="row"> Сompany</th>
                  <td className="text-start p-1 m-0">
                    <strong>{data?.client_name}</strong>
                  </td>
                </tr>
                <tr className="p-0 m-0">
                  <th scope="row">{t('VisitReportsPDF.header.object')}</th>
                  <td className="text-start p-1 m-0" id="size-max">
                    <strong>{data?.object}</strong>
                  </td>
                </tr>
                <tr className="p-0 m-0">
                  <th scope="row">{t('VisitReportsPDF.header.fisical-person')}</th>
                  <td className="text-start p-1 m-0">
                    <strong>{data?.object_address}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="row">
            <div className="col-6">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                <thead className="">
                  <tr>
                    <th className="text-center" scope="col">
                      {t('VisitReportsPDF.header.performer')}
                    </th>
                  </tr>
                </thead>
                <tbody className="list form-check-all" id="trap_table">
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-6">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                <thead className="">
                  <tr>
                    <th className="text-center" scope="col">
                      {t('VisitReportsPDF.header.customer')}
                    </th>
                  </tr>
                </thead>
                <tbody className="list form-check-all" id="trap_table">
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="row border-bottom-dashed border-light my-3" />
          {data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.MCT}
              startDate={data.start_date}
              endDate={data.end_date}
              zoom={false}
            />
          )}
          <div className="row border-bottom-dashed border-light my-3" />
          {data?.start_date && data?.end_date && (
            <SBSChart slug={slug || ''} startDate={data.start_date} endDate={data.end_date} zoom={false} />
          )}
          {data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.SMST}
              startDate={data.start_date}
              endDate={data.end_date}
              zoom={false}
            />
          )}
          <div className="row border-bottom-dashed border-light my-3" />
          {data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.SRST}
              startDate={data.start_date}
              endDate={data.end_date}
              zoom={false}
            />
          )}
          <div className="row border-bottom-dashed border-light my-3" />
          <HeatMaps startDate={data?.start_date || ''} endDate={data?.end_date || ''} />
          <div className="row border-bottom-dashed border-light my-3" />
          <div className="row mt-5">
            <div className="row text-center">
              <h5>
                {t('EcnounterList.pdf-title.report')} <strong>"{t('EcnounterList.pdf-title.report-name')}"</strong>
              </h5>
            </div>
            <div className="row">
              <div className="">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('EcnounterList.accordion.accordion-inner.encount-data')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('EcnounterList.accordion.accordion-inner.pest.pest-name-traces')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('EcnounterList.accordion.accordion-inner.witness')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('EcnounterList.accordion.accordion-inner.inspection-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('EcnounterList.accordion.accordion-inner.inspector')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.encounter_list.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.encounter_list.map(enc => (
                        <tr className="align-content-center">
                          <td className="text-center">{enc.discovery_date}</td>
                          {enc.pest_name ? (
                            <td className="text-center">{enc.pest_name}</td>
                          ) : (
                            <td className="text-center">{enc.pest_traces}</td>
                          )}
                          <td className="text-center">{enc.witness}</td>
                          {enc.inspection_date === 'Не інспектовано' ? (
                            <td className="text-center text-danger">
                              {dayjs(enc.inspection_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                            </td>
                          ) : (
                            <td className="text-center">
                              {dayjs(enc.inspection_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                            </td>
                          )}
                          {enc.inspector === 'Не інспектовано' ? (
                            <td className="text-center text-danger">{enc.inspector}</td>
                          ) : (
                            <td className="text-center">{enc.inspector}</td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* <div className="row mt-2">
            <div className="row text-center">
              <h5>
                {t('EcnounterList.pdf-title.report')} <strong>"{t('TrapsDataList.title')}"</strong>
              </h5>
            </div>
            <div className="row">
              <div className="mx-0 px-0">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('TrapsDataList.accordion.accordion-inner.sequence-number')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('TrapsDataList.accordion.accordion-inner.serial-number')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('TrapsDataList.accordion.accordion-inner.last-monitoring-msg')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('TrapsDataList.accordion.accordion-inner.next-recieved-msg')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('TrapsDataList.accordion.accordion-inner.lost-msgs')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.trap_data_list.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.trap_data_list.map(trap => (
                        <tr className="align-content-center">
                          <td className="text-center">{trap.trap_number}</td>
                          <td className="text-center">{trap.serial_number}</td>
                          <td className="text-center">
                            {dayjs(trap.offline_start).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                          </td>
                          <td className="text-center">
                            {dayjs(trap.offline_end).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                          </td>
                          <td className="text-center">{trap.lost_msgs}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div> */}
          <TrapConnectivityGaps data={data} trapType={TrapType.MCT} />
          <TrapConnectivityGaps data={data} trapType={TrapType.SMST} />
          <TrapConnectivityGaps data={data} trapType={TrapType.SRST} />
          <div className="row mt-2">
            <div className="row text-center">
              <h5>
                {t('EcnounterList.pdf-title.report')} <strong>"{t('VulnerabilityList.title')}"</strong>
              </h5>
            </div>
            <div className="row">
              <div className="mx-0 px-0">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.type')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.criticality')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.resonsive-person')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.inspector')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.create-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.last-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VulnerabilityList.accordion.accordion-inner.fix-date')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.vulnetability_list.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.vulnetability_list.map(vul => (
                        <tr className="align-content-center">
                          <td className="text-center">{vul.category}</td>
                          {vul.criticality === 'Критичний' ? (
                            <td className="text-center text-danger">{vul.criticality}</td>
                          ) : (
                            <td className="text-center">{vul.criticality}</td>
                          )}
                          <td className="text-center">{vul.responsible}</td>
                          <td className="text-center">{vul.inspector}</td>
                          <td className="text-center">{vul.create_date}</td>
                          <td className="text-center">{vul.last_date}</td>
                          {vul.fix_date === 'Не усунено!' ? (
                            <td className="text-center text-danger">{vul.fix_date}</td>
                          ) : (
                            <td className="text-center">{vul.fix_date}</td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <InnerMctMonitoring slug={slug || ''} startDate={data?.start_date || ''} endDate={data?.end_date || ''} />
          <InnerSbsMonitoring
            slug={slug || ''}
            startDate={data?.start_date || ''}
            endDate={data?.end_date || ''}
            filterType="default"
          />
          <InnerSTMonitoring
            slug={slug || ''}
            trapType={TrapTypeMonitoring.SMST}
            startDate={data?.start_date || ''}
            endDate={data?.end_date || ''}
          />
          <InnerSTMonitoring
            slug={slug || ''}
            trapType={TrapTypeMonitoring.SRST}
            startDate={data?.start_date || ''}
            endDate={data?.end_date || ''}
          />
          <div className="row mt-5">
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.report-generated')}</th>
                    <td className="text-start p-1 m-0">
                      {dayjs(data?.call_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.pest-technician-full-name')}</th>
                    <td className="text-center" style={{ width: '60%' }}>
                      <i>{data?.pest_signatured_by ? data?.pest_signatured_by.username : ''}</i>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.signature')}</th>
                    <td className={data?.pest_signatured_by ? 'text-center' : 'text-start'} style={{ width: '60%' }}>
                      {data?.pest_signatured_by ? (
                        <img
                          style={{ maxHeight: '70px' }}
                          src={`${process.env.REACT_APP_BASE_DJANGO_URL}${data?.pest_signatured_by.signature}`}
                          alt="Pest employee signature"
                        />
                      ) : (
                        userData &&
                        ['pest_client_employee', 'pest_client_manager', 'pest_client'].includes(userData.role) &&
                        (userData.user_signature ? (
                          <MLButton
                            innerText={t('VisitReportsPDF.footer.btn-sign')}
                            isGhost
                            isItalic
                            icon="ri-quill-pen-fill fs-16"
                            color="success"
                            onClick={() => {
                              signPeriodReportByEmployee({
                                signPeriodReportByPestEmployee: { pest_signatured_by: null },
                                id: id || '',
                                slug: slug || '',
                              });
                            }}
                          />
                        ) : (
                          <MLButton
                            onClick={() => onManageSignatureModalOpen(slug || '', data?.id || 0, false, 'period')}
                            innerText={t('VisitReportsPDF.footer.btn-add-sign')}
                            color="success"
                            isGhost
                            icon="ri-quill-pen-fill"
                            fontSize={14}
                          />
                        ))
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <SignatureModal refetch={refetch} />
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.full-name-of-responsoble-person')} </th>
                    <td className="text-center" style={{ width: '60%' }}>
                      <i>{data?.customer_signatured_by ? data?.customer_signatured_by.username : ''}</i>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.signature')}</th>
                    <td className={data?.pest_signatured_by ? 'text-center' : 'text-start'} style={{ width: '60%' }}>
                      {data?.customer_signature ? (
                        <img
                          style={{ maxHeight: '70px' }}
                          src={data?.customer_signature}
                          alt="Pest employee signature"
                        />
                      ) : userData &&
                        ['pest_client_employee', 'pest_client_manager', 'pest_client'].includes(userData.role) ? (
                        <MLButton
                          onClick={() => onManageSignatureModalOpen(slug || '', data?.id || 0, true, 'period')}
                          innerText="Sign"
                          color="success"
                          isGhost
                          icon="ri-quill-pen-fill"
                          fontSize={14}
                        />
                      ) : (
                        <a
                          className="btn btn-ghost-success"
                          target="_blank"
                          href={`${process.env.REACT_APP_BASE_DJANGO_URL}/user/${userData?.slug}/update`}
                          rel="noopener noreferrer"
                        >
                          {t('VisitReportsPDF.footer.btn-add-sign')}
                        </a>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PeriodDocsPDF;
